/**
 * Review inbox "Überprüfung" (RAK-426, RAK-428): the cards a bot asks the user to decide or
 * accept, and the pure rules the page follows. Shapes come from the reviews contract
 * (packages/contracts/src/review.ts, RAK-425); the data comes from a ReviewInboxSource.
 */
import {
  type PendingAsk,
  REVIEW_ACCEPT_OPTIONS,
  REVIEW_TEXT_MAX_LENGTH,
  type ReviewKind,
  type ReviewRequest,
} from "@rakazo/contracts";

export type {
  PendingAsk,
  ReviewKind,
  ReviewPreview,
  ReviewRequest,
  ReviewStatus,
} from "@rakazo/contracts";

/** What reviews.list returns: cards in the user's order and open chat cards, newest first. */
export type ReviewInbox = { items: ReviewRequest[]; asks: PendingAsk[] };

/** Answer as the page sends it: the chosen option, a comment, or both. */
export type ReviewAnswerInput = { option?: string; comment?: string };

/** Answer values of an acceptance card (ok, zurueck); the page shows "Abnehmen" and "Zurückgeben". */
export const ACCEPT_OPTIONS = REVIEW_ACCEPT_OPTIONS;

/** Most characters a comment or a question may have (REVIEW_TEXT_MAX_LENGTH of the contract). */
export const REVIEW_COMMENT_MAX = REVIEW_TEXT_MAX_LENGTH;

/** Port of the page; the reviews router answers the same calls (contract, "Aufrufe"). */
export interface ReviewInboxSource {
  /** Open and deferred cards of the user plus open chat cards. */
  list(): Promise<ReviewInbox>;
  answer(id: string, input: ReviewAnswerInput): Promise<ReviewRequest>;
  defer(id: string): Promise<ReviewRequest>;
  reopen(id: string): Promise<ReviewRequest>;
  /** Moves a card before another one; without beforeId to the end. */
  reorder(id: string, beforeId: string | null): Promise<ReviewRequest>;
  /** Open cards plus open chat cards, for the sidebar badge. */
  count(): Promise<number>;
  /** A question to the asking bot, written into the card's thread (contract, "Rückfrage-Chat"). */
  ask(item: Pick<ReviewRequest, "botId" | "sessionId">, text: string): Promise<void>;
}

/**
 * True when the card is gone for this user: already answered or withdrawn (CONFLICT) or unknown
 * (NOT_FOUND). The page then reloads and says so instead of showing the server text.
 */
export function isReviewGone(cause: unknown): boolean {
  if (typeof cause !== "object" || cause === null || !("code" in cause)) return false;
  return cause.code === "CONFLICT" || cause.code === "NOT_FOUND";
}

/** The list on the left: open cards in the server's order (position), deferred ones below. */
export function inboxSections(items: readonly ReviewRequest[]) {
  return {
    open: items.filter((item) => item.status === "offen"),
    deferred: items.filter((item) => item.status === "zurueckgestellt"),
  };
}

/** Order of the whole list as shown: open first, then deferred. */
export function inboxOrder(items: readonly ReviewRequest[]): ReviewRequest[] {
  const { open, deferred } = inboxSections(items);
  return [...open, ...deferred];
}

/**
 * The card to show after one was answered or deferred: the next open card after it in the old
 * order, else the open card before it, else null (inbox empty).
 */
export function nextOpenAfter(items: readonly ReviewRequest[], doneId: string): string | null {
  const open = inboxSections(items).open;
  const index = open.findIndex((item) => item.id === doneId);
  if (index === -1) return open[0]?.id ?? null;
  return open[index + 1]?.id ?? open[index - 1]?.id ?? null;
}

/** The neighbour for j/k in the shown order; stays at the end instead of wrapping. */
export function stepInOrder(
  items: readonly ReviewRequest[],
  currentId: string | null,
  step: 1 | -1,
): string | null {
  const order = inboxOrder(items);
  if (order.length === 0) return null;
  const index = order.findIndex((item) => item.id === currentId);
  if (index === -1) return order[0]?.id ?? null;
  const next = Math.min(order.length - 1, Math.max(0, index + step));
  return order[next]?.id ?? null;
}

/** A decision needs an option, an acceptance its ok/back, a comment card some text. */
export function canSubmitAnswer(kind: ReviewKind, option: string | null, comment: string): boolean {
  if (comment.length > REVIEW_COMMENT_MAX) return false;
  if (kind === "kommentar") return comment.trim().length > 0;
  return option !== null;
}

/** Options as the page offers them; an acceptance always has ok and back. */
export function answerOptions(item: Pick<ReviewRequest, "kind" | "options">): readonly string[] {
  if (item.kind === "abnahme") return ACCEPT_OPTIONS;
  if (item.kind === "kommentar") return [];
  return item.options;
}

/** Where "Zur Sitzung" goes: the asking session, else the bot's chat. Also used for chat cards. */
export function reviewOriginPath(item: Pick<ReviewRequest, "botId" | "sessionId">): string {
  const bot = encodeURIComponent(item.botId);
  return item.sessionId ? `/app/${bot}/s/${encodeURIComponent(item.sessionId)}` : `/app/${bot}`;
}

/** Only http(s) links from a bot become clickable; anything else is shown as text. */
export function safeExternalUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : null;
  } catch {
    return null;
  }
}
